import { useEffect, useMemo } from "react";
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import { MapPin } from "lucide-react";

const OSM_URL = "https://tile.openstreetmap.org/{z}/{x}/{y}.png";
const OSM_ATTR =
  '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';

// Small amber-outline pin so users can see the current selection clearly.
const pinIcon = L.divIcon({
  html: `<div style="
    width:28px;height:28px;border-radius:50% 50% 50% 0;
    background:#f59e0b;border:3px solid #fff;transform:rotate(-45deg);
    box-shadow:0 3px 8px rgba(0,0,0,.5);
  "><div style="
    width:8px;height:8px;background:#fff;border-radius:50%;
    position:absolute;top:6px;left:7px;
  "></div></div>`,
  iconSize: [28, 28],
  iconAnchor: [14, 28],
  className: "",
});

function ClickHandler({ onPick }) {
  useMapEvents({
    click(e) {
      onPick(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
}

function Recenter({ lat, lng }) {
  const map = useMap();
  useEffect(() => {
    if (typeof lat === "number" && typeof lng === "number" && !isNaN(lat) && !isNaN(lng)) {
      map.flyTo([lat, lng], Math.max(map.getZoom(), 13), { duration: 0.6 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lat, lng]);
  return null;
}

/**
 * Small pin-drop map for the admin edit/create dialog. Click anywhere on the
 * map to set the depot location. Uses the same OSM-inverted-dark style as the
 * main map so it feels consistent.
 */
export function PinDropMap({ latitude, longitude, onPick, height = 240 }) {
  const lat = latitude === "" || latitude == null ? null : parseFloat(latitude);
  const lng = longitude === "" || longitude == null ? null : parseFloat(longitude);
  const validPin =
    lat != null && lng != null && !isNaN(lat) && !isNaN(lng) &&
    lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;

  const initialCenter = useMemo(
    () => (validPin ? [lat, lng] : [39.8283, -98.5795]),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  const initialZoom = validPin ? 14 : 4;

  return (
    <div className="relative rounded-lg overflow-hidden border border-white/10">
      <MapContainer
        center={initialCenter}
        zoom={initialZoom}
        style={{ height, width: "100%" }}
        scrollWheelZoom={true}
        data-testid="pin-drop-map"
      >
        <TileLayer url={OSM_URL} attribution={OSM_ATTR} className="leaflet-tile-dark" />
        <ClickHandler onPick={onPick} />
        {validPin && <Marker position={[lat, lng]} icon={pinIcon} />}
        {validPin && <Recenter lat={lat} lng={lng} />}
      </MapContainer>
      <div className="absolute top-2 left-2 bg-black/70 backdrop-blur px-2.5 py-1 rounded-md text-[10px] uppercase tracking-[0.2em] text-amber-400 pointer-events-none flex items-center gap-1.5 z-[500]">
        <MapPin className="w-3 h-3" />
        {validPin ? "Click to move pin" : "Click on the map to drop a pin"}
      </div>
    </div>
  );
}
